import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Link,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import imgUrl from '../assets/images/imgUrl';
import { useNavigate } from "react-router";
import { apiServices } from '../services/apiServices';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Close } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { loadStripe } from '@stripe/stripe-js';
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  loginUser,
  checkUserEmail,
  setCurrentUser,
  setShowPasswordField,
  clearError,
  sendLoginToken,
  validateLoginToken,
  setOtpEmail,
  setOtpSent,
  clearOtpState,
  setOtpError,
  setShowPaymentModal,
  setPaymentStatus,
  setRequiresPayment
} from '../store/slices/authSlice';

// Stripe configuration
//const stripePromise = loadStripe('pk_test_51RtjU7KTDUE4cDSIeDRD4296VQ2RMb0C9VL7wZg2rierEt3O9hwpLALOuB3dTSrAlyiMbo8UuqF1qnvwmvenXiDJ00wkfc6EDs');
const stripePromise = loadStripe('pk_live_51QSuqmCco9ltycd0RKgPRRacPIukV2k7Eo86CqrHkBh5k3LYYMsSHy7s2tk9gPCZx9Gt9lPYV37mIy1XsDcjt4Tm008KTFmJXN')

const options = {
  mode: 'payment',
  amount: 2100,
  currency: 'usd',
  appearance: {
    theme: 'stripe',
    variables: {
      colorPrimary: '#3A8139',
    },
  },
};

// Payment Form Component
const CheckoutForm = ({ onPaymentSuccess, onPaymentError, email, onPaymentStart, onPaymentComplete }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (elements == null) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    // Notify parent component that payment has started
    onPaymentStart && onPaymentStart();

    try {
      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message);
        onPaymentError && onPaymentError(submitError.message);
        return;
      }

      // Create the PaymentIntent and obtain clientSecret from your server endpoint
      const response = await apiServices.postAPI('create-payment-intent', {
        amount: 2100,
        currency: 'usd',
        email: email
      });

      console.log('Payment intent response:', response);

      if (!response.data || !response.data.clientSecret) {
        throw new Error('No client secret received from server');
      }

      const { clientSecret } = response.data;

      // Prevent any redirects and handle payment confirmation
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required' // Prevent automatic redirects
      });

      if (error) {
        setErrorMessage(error.message);
        onPaymentError && onPaymentError(error.message);
      } else {
        console.log('Payment successful!');
        // Verify payment was actually successful
        const paymentIntent = await stripe.retrievePaymentIntent(clientSecret);
        console.log('Payment intent status:', paymentIntent.paymentIntent.status);

        if (paymentIntent.paymentIntent.status === 'succeeded') {
          console.log('Payment confirmed successful!');
          // Call success callback without any page refresh
          onPaymentSuccess && onPaymentSuccess();
        } else {
          console.log('Payment not yet succeeded, status:', paymentIntent.paymentIntent.status);
          setErrorMessage('Payment is being processed. Please wait...');
          onPaymentError && onPaymentError('Payment is being processed. Please wait...');
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      setErrorMessage(errorMessage);
      onPaymentError && onPaymentError(errorMessage);
    } finally {
      setIsProcessing(false);
      // Notify parent component that payment processing is complete
      onPaymentComplete && onPaymentComplete();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <Button
        variant="contained"
        sx={{
          mt: 2,
          background: 'linear-gradient(to right, #214F7D, #3A8139)',
          color: 'white',
          borderRadius: "50px",
          fontFamily: 'var(--default-font)',
          width: 'auto',
          px: 4,
          mx: 'auto',
          display: 'block'
        }}
        type="submit"
        disabled={!stripe || !elements || isProcessing}>
        {isProcessing ? 'Processing...' : 'Complete Payment'}
      </Button>
      {/* Show error message to your customers */}
      {errorMessage && (
        <Typography color="error" sx={{ mt: 1, fontSize: '14px' }}>
          {errorMessage}
        </Typography>
      )}
    </form>
  );
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // OTP related state
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [otpSuccess, setOtpSuccess] = useState(false);

  // Payment modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);

  // Payment processing state
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [updatingPaymentStatus, setUpdatingPaymentStatus] = useState(false);
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(true); // Track if status update was successful

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  // Handle payment modal
  const handlePaymentModal = (userData) => {
    if (userData.usertype != "Entity" && (userData.payment_status && userData.payment_status == 'PENDING')) {
      if (window.cordova) {
        Swal.fire({
          title: 'Warning',
          text: 'Please complete the pending payment to log in.',
          icon: 'warning',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          allowOutsideClick: true,
          buttonsStyling: false,
        }).then((result) => {
          if (result.isConfirmed) {
            window.open('https://i-am-peacekeeper.com/login', '_blank');
          }
        });

      } else {
        setPaymentModalData(userData);
        setPaymentModalOpen(true);
        return true; // Indicates payment modal was shown
      }
    }
    return false; // No payment modal needed
  };

  // Payment callback functions
  const handlePaymentStart = () => {
    setPaymentLoading(true);
    setPaymentSuccess(false);
  };

  /**
   * Handles successful payment completion
   * 1. Updates payment status in backend via API
   * 2. Shows success message to user
   * 3. Handles any errors during status update gracefully
   */

  const handlePaymentSuccess = async () => {
    setPaymentLoading(false);
    setUpdatingPaymentStatus(true);

    try {
      // Update payment status in the backend with timeout
      const updatePromise = apiServices.putAPI('update-payment-status-by-email', {
        email: paymentModalData?.email || email,
        status: 'COMPLETED'
      });

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      const updateResponse = await Promise.race([updatePromise, timeoutPromise]);

      console.log('Payment status update response:', updateResponse);

      setUpdatingPaymentStatus(false);
      setStatusUpdateSuccess(true);
      setPaymentSuccess(true);

      // Success is now handled by the modal UI, no need for SweetAlert
    } catch (error) {
      console.error('Error updating payment status:', error);

      setUpdatingPaymentStatus(false);
      setStatusUpdateSuccess(false);
      setPaymentSuccess(true);

      // Error is now handled by the modal UI, no need for SweetAlert
    }
  };

  const handlePaymentError = (errorMessage) => {
    setPaymentLoading(false);
    console.error('Payment error:', errorMessage);
  };

  const handlePaymentComplete = () => {
    setPaymentLoading(false);
  };

  // Retry payment status update
  const handleRetryPaymentStatusUpdate = async () => {
    if (!paymentModalData?.email && !email) {
      console.error('No email available for payment status update');
      return;
    }

    setUpdatingPaymentStatus(true);

    try {
      const updatePromise = apiServices.putAPI('update-payment-status-by-email', {
        email: paymentModalData?.email || email,
        status: 'COMPLETED'
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      const updateResponse = await Promise.race([updatePromise, timeoutPromise]);

      console.log('Payment status update retry response:', updateResponse);

      setUpdatingPaymentStatus(false);

      // Success - close the modal
      resetPaymentModal();
    } catch (error) {
      console.error('Error retrying payment status update:', error);

      setUpdatingPaymentStatus(false);

      // Error is handled by the modal UI state, no need for SweetAlert
    }
  };

  // Show payment form
  const handleShowPaymentForm = () => {
    setShowPaymentForm(true);
  };

  // Reset payment modal states
  const resetPaymentModal = () => {
    setPaymentModalOpen(false);
    setShowPaymentForm(false);
    setPaymentSuccess(false);
    setPaymentLoading(false);
    setUpdatingPaymentStatus(false);
    setStatusUpdateSuccess(true);
    setPaymentModalData(null);
  };

  // Redux state
  const {
    isLoading,
    error,
    currentUser,
    showPasswordField,
    isAuthenticated,
    otpError,
    paymentStatus,
    requiresPayment,
    showPaymentModal
  } = useSelector((state) => state.auth);

  // Watch for payment status changes
  useEffect(() => {
    if (requiresPayment && currentUser) {
      handlePaymentModal(currentUser);
    }
  }, [requiresPayment, currentUser]);

  const checkEmail = async () => {
    console.log('Email:', email);

    if (!email.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter your email address',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter a valid email address',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    try {
      // Clear any previous errors
      dispatch(clearError());

      // Dispatch the check email action
      const result = await dispatch(checkUserEmail(email));

      if (checkUserEmail.fulfilled.match(result)) {
        const response = result.payload;
        console.log('Check email response:', response);

        if (response.exists === true) {
          // User exists in database
          const user = response.user;
          console.log('User found:', user);

          // Check if user account is inactive
          if (user.activestatus === 'N') {
            Swal.fire({
              title: 'Account Inactive',
              text: 'Your account has been deactivated. Please contact support for assistance.',
              icon: 'error',
              showConfirmButton: true,
              confirmButtonText: 'OK',
              customClass: {
                popup: 'minimal-popup',
                confirmButton: 'minimal-button',
              },
              backdrop: false,
              allowOutsideClick: true,
              buttonsStyling: false,
            });
            return; // Stop the login flow
          }

          // Check payment status first
          if (handlePaymentModal(user)) {
            // Payment modal was shown, don't proceed with login
            return;
          }
          if (user.hasPassword === true) {
            // User has password, show password field
            dispatch(setCurrentUser(user));
            dispatch(setShowPasswordField(true));

            // Swal.fire({
            //   title: 'User Found',
            //   text: 'Please enter your password to continue.',
            //   icon: 'info',
            //   showConfirmButton: true,
            //   confirmButtonText: 'OK',
            //   customClass: {
            //     popup: 'minimal-popup',
            //     confirmButton: 'minimal-button',
            //   },
            //   backdrop: false,
            //   allowOutsideClick: true,
            //   buttonsStyling: false,
            // });
          } else {
            // User exists but no password, send validation email
            try {
              // Send validation email for users without password
              const validationResponse = await apiServices.postAPI('send-validation-email', {
                userName: user.name,
                to: email
              });

              console.log('Validation email response:', validationResponse);

              Swal.fire({
                title: 'Success',
                text: 'Validation email sent successfully! Please check your email.',
                icon: 'success',
                showConfirmButton: true,
                confirmButtonText: 'OK',
                customClass: {
                  popup: 'minimal-popup',
                  confirmButton: 'minimal-button',
                },
                backdrop: false,
                allowOutsideClick: true,
                buttonsStyling: false,
              });

              // Navigate to setup password page after successful email send
              navigate(`/setuppassword?email=${encodeURIComponent(email)}`);
            } catch (validationError) {
              navigate(`/setuppassword?email=${encodeURIComponent(email)}`);
              console.error('Error sending validation email:', validationError);
              Swal.fire({
                title: 'Error',
                text: 'Failed to send validation email. Please try again.',
                icon: 'error',
                showConfirmButton: true,
                confirmButtonText: 'OK',
                customClass: {
                  popup: 'minimal-popup',
                  confirmButton: 'minimal-button',
                },
                backdrop: false,
                allowOutsideClick: true,
                buttonsStyling: false,
              });
            }
          }


        } else if (response.exists === false) {
          // User not found in database
          Swal.fire({
            title: 'User Not Found',
            text: 'This email is not registered. ',
            icon: 'warning',
            customClass: {
              popup: 'minimal-popup',
              confirmButton: 'minimal-button',
              cancelButton: 'minimal-button',
            },
            backdrop: false,
            allowOutsideClick: true,
            buttonsStyling: false,
          }).then((result) => {
            if (result.isConfirmed) {
              // Navigate to registration page
              // navigate("/register");
            }
          });
        } else {
          throw new Error('Invalid response format from server');
        }
      } else {
        // Handle rejected action
        throw new Error(result.payload?.message || 'Email check failed');
      }
    } catch (error) {
      console.error('Error checking email:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to check email. Please try again.',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
    }
  };

  const loginWithPassword = async () => {
    if (!password.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter your password',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    try {
      // Clear any previous errors
      dispatch(clearError());

      // Dispatch the login action
      const result = await dispatch(loginUser({ email, password }));

      if (loginUser.fulfilled.match(result)) {
        // Login successful - get FCM token for push notifications
        if (window.cordova && window.FirebasePlugin) {
          try {
            // Grant permission for notifications
            window.FirebasePlugin.grantPermission();

            // Get FCM token
            window.FirebasePlugin.getToken(function (token) {
              console.log("FCM Token: " + token);
              // Send token to your server using update-fcm-id API
              apiServices.putAPI('update-fcm-id', {
                email: email,
                fcmid: token
              }).then(response => {
                console.log("FCM token saved successfully:", response);
              }).catch(error => {
                console.error("Error saving FCM token:", error);
              });
            }, function (error) {
              console.error("Error getting token", error);
            });
          } catch (error) {
            console.error("Firebase Plugin error:", error);
          }
        }

        // Check usertype and redirect accordingly
        const userData = result.payload.user || result.payload;
        
        // Regenerate visiting card if user data is available
        if (userData?.idNumber || userData?.peacekeeper_id) {
          const idForRegeneration = userData.idNumber || userData.peacekeeper_id;
          // Regenerate visiting card in background (don't wait for it)
          apiServices.putAPI(`update-visiting-card/${idForRegeneration}`, {
            forceRegenerate: true
          }).catch(err => {
            console.log('Visiting card regeneration failed (non-critical):', err);
          });
        }
        
        if (userData?.usertype === 'Entity') {
          // Store admin session data in localStorage
          localStorage.setItem('adminData', JSON.stringify(userData));
          localStorage.setItem('isAdminLoggedIn', 'true');
          navigate("/admin/dashboard");
        } else {
          navigate("/dashboard");
        }
      } else {
        // Login failed
        throw new Error(result.payload?.message || 'Invalid password. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);

      let errorMessage = 'Invalid password. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        title: 'Login Failed',
        text: errorMessage,
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    dispatch(setShowPasswordField(false));
    dispatch(setCurrentUser(null));
    dispatch(clearError());
  };

  // OTP related functions
  const handleSendOtp = async () => {
    if (!email.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter your email address',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter a valid email address',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    try {
      setOtpLoading(true);
      dispatch(clearError());

      // First check email and payment status using the same API as regular login
      const emailCheckResult = await dispatch(checkUserEmail(email));

      if (checkUserEmail.fulfilled.match(emailCheckResult)) {
        const response = emailCheckResult.payload;
        console.log('Check email response for OTP:', response);

        if (response.exists === true) {
          const user = response.user;
          console.log('User found for OTP:', user);

          // Check payment status first
          if (handlePaymentModal(user)) {
            // Payment modal was shown, don't proceed with OTP
            setOtpLoading(false);
            return;
          }

          // No payment required, proceed with sending OTP
          dispatch(setOtpError(null));

          // Set OTP email in Redux state
          dispatch(setOtpEmail(email));

          // Send OTP
          const otpResult = await dispatch(sendLoginToken(email));

          if (sendLoginToken.fulfilled.match(otpResult)) {
            setOtpSent(true);
            startResendTimer();
            setShowOtpModal(true);
          } else {
            throw new Error(otpResult.payload?.message || 'Failed to send OTP');
          }
        } else if (response.exists === false) {
          // User not found in database
          Swal.fire({
            title: 'User Not Found',
            text: 'This email is not registered.',
            icon: 'warning',
            customClass: {
              popup: 'minimal-popup',
              confirmButton: 'minimal-button',
              cancelButton: 'minimal-button',
            },
            backdrop: false,
            allowOutsideClick: true,
            buttonsStyling: false,
          });
        } else {
          throw new Error('Invalid response format from server');
        }
      } else {
        // Handle rejected action
        throw new Error(emailCheckResult.payload?.message || 'Email check failed');
      }
    } catch (error) {
      console.error('Error in OTP flow:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to process OTP request. Please try again.',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter the OTP',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    if (otp.length < 4) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter a valid OTP',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    try {
      setOtpLoading(true);
      dispatch(setOtpError(null));

      const result = await dispatch(validateLoginToken({ email, otp: otp }));

      if (validateLoginToken.fulfilled.match(result)) {
        // OTP validation successful
        console.log('OTP validation successful');

        // Check usertype and redirect accordingly
        const userData = result.payload.user || result.payload;
        
        // Regenerate visiting card if user data is available
        if (userData?.idNumber || userData?.peacekeeper_id) {
          const idForRegeneration = userData.idNumber || userData.peacekeeper_id;
          // Regenerate visiting card in background (don't wait for it)
          apiServices.putAPI(`update-visiting-card/${idForRegeneration}`, {
            forceRegenerate: true
          }).catch(err => {
            console.log('Visiting card regeneration failed (non-critical):', err);
          });
        }
        
        if (userData?.usertype === 'Entity') {
          // Store admin session data in localStorage
          localStorage.setItem('adminData', JSON.stringify(userData));
          localStorage.setItem('isAdminLoggedIn', 'true');
          navigate("/admin/dashboard");
        } else {
          navigate("/dashboard");
        }

        // Show brief success feedback in modal before closing
        setOtpLoading(false);
        setOtpSuccess(true);

        // Close modal after brief success display
        setTimeout(() => {
          setShowOtpModal(false);
          setOtp('');
          setOtpSent(false);
          setOtpSuccess(false);
        }, 1000);
      } else {
        throw new Error(result.payload?.message || 'OTP validation failed');
      }
    } catch (error) {
      console.error('Error validating OTP:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Invalid OTP. Please try again.',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    try {
      setOtpLoading(true);
      dispatch(setOtpError(null));

      const result = await dispatch(sendLoginToken(email));

      if (sendLoginToken.fulfilled.match(result)) {
        Swal.fire({
          title: 'Success',
          text: 'OTP resent successfully!',
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          allowOutsideClick: true,
          buttonsStyling: false,
        });

        startResendTimer();
      } else {
        throw new Error(result.payload?.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to resend OTP. Please try again.',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const startResendTimer = () => {
    setResendTimer(60); // 60 seconds
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setOtp('');
    setOtpSent(false);
    setOtpSuccess(false);
    dispatch(clearOtpState());
  };

  // Handle resend timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Handle authentication state changes - redirect immediately if already logged in
  useEffect(() => {
    // Check if user is already authenticated and redirect immediately
    if (isAuthenticated && currentUser) {
      // User is already logged in, check usertype and redirect accordingly
      if (currentUser.usertype === 'Entity') {
        navigate("/admin/dashboard", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
      return; // Exit early to prevent rendering login form
    }
  }, [isAuthenticated, currentUser, navigate]);

  // Handle errors from Redux
  useEffect(() => {
    if (error) {
      Swal.fire({
        title: 'Error',
        text: error,
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Handle OTP errors from Redux
  useEffect(() => {
    if (otpError && showOtpModal) {
      // OTP error will be displayed in the modal, no need for SweetAlert
      console.log('OTP Error:', otpError);
    }
  }, [otpError, showOtpModal]);

  // If user is already authenticated, don't render login form (NavigationGuard will redirect)
  if (isAuthenticated && currentUser) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: "hidden"
      }}
      className="LoginSection"
    >

      <Paper elevation={5} sx={{ p: { xs: 3, sm: 4 }, width: { xs: 320, sm: 360 }, textAlign: 'center', borderRadius: 2 }} className='UserSubSection'>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            mb: 2
          }}
        >
          {/* Back to Home Button */}
          <Button
            sx={{
              position: 'absolute',
              left: { xs: 0, sm: 0 },
              top: { xs: -12, sm: -8 },
              color: '#214F7D',
              textTransform: 'none',
              fontSize: { xs: '12px', sm: '14px' },
              fontWeight: 500,
              minWidth: 'auto',
              px: { xs: 1, sm: 2 },
              display: { xs: 'none', sm: 'flex' },
              '&:hover': {
                backgroundColor: 'rgba(33, 79, 125, 0.08)'
              },
              '& .MuiButton-startIcon': {
                marginRight: { xs: 0.5, sm: 1 },
                '& svg': {
                  width: { xs: '18px', sm: '20px' },
                  height: { xs: '18px', sm: '20px' }
                }
              }
            }}
            onClick={() => navigate('/')}
            startIcon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 20V14H14V20H19V12H22L12 3L2 12H5V20H10Z" fill="currentColor" />
              </svg>
            }
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Back to Home
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Back
            </Box>
          </Button>

          {/* Centered Logo */}
          <img src={imgUrl.peaceMovImg} alt="Logo" width="100" style={{ cursor: 'pointer' }} onClick={() => navigate("/")} />
        </Box>

        {/* Heading */}
        <Typography variant="h6" fontWeight="400" mb={0} className='loginHeading'>
          LOGIN
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Login for I Am Peacekeeper only
        </Typography>

        <Typography sx={{ textAlign: "left" }}>Email Address</Typography>
        {/* Email Input */}
        <TextField
          fullWidth
          label=""
          placeholder='Please enter email'
          type="email"
          required
          variant="outlined"
          margin="dense"
          size="small"
          sx={{ mb: 2 }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        {/* Check Email Button - Hide when password field is shown */}
        {!showPasswordField && (
          <Button
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              mt: 2,
              background: 'linear-gradient(to right, #214F7D, #3A8139)',
              color: 'white',
              borderRadius: "50px"
            }}
            onClick={() => checkEmail()}
          >
            {isLoading ? 'Checking...' : 'Login'}
          </Button>
        )}

        {/* Password Field - Show only when user has password */}
        {showPasswordField && (
          <>
            <Typography sx={{ textAlign: "left", mt: 2 }}>Password</Typography>
            <TextField
              fullWidth
              placeholder="Please enter password"
              type={showPassword ? 'text' : 'password'}
              required
              variant="outlined"
              margin="dense"
              size="small"
              sx={{ mb: 2 }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePassword}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Login with Password Button */}
            <Button
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 2,
                background: 'linear-gradient(to right, #214F7D, #3A8139)',
                color: 'white',
                borderRadius: "50px"
              }}
              onClick={() => loginWithPassword()}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>

            {/* Forgot Password Link */}
            <Link
              underline="hover"
              sx={{
                display: 'block',
                mt: 2,
                fontSize: 14,
                textDecoration: "underline",
                color: '#666',
                textAlign: 'center'
              }}
              className='cursor-pointer'
              onClick={() => navigate("/forgotpassword")}
            >
              Forgot Password?
            </Link>

            {/* Back Button */}
            <Button
              fullWidth
              variant="outlined"
              sx={{
                mt: 1,
                borderColor: '#214F7D',
                color: '#214F7D',
                borderRadius: "50px",
                display: { xs: 'none', sm: 'flex' }
              }}
              onClick={resetForm}
            >
              Back
            </Button>
          </>
        )}

        {/* OR Separator and Other Options - Hide when password field is shown */}
        {!showPasswordField && (
          <>
            {/* OR Separator */}
            <Typography variant="body2" mt={2} mb={1} color="text.secondary">
              OR
            </Typography>

            {/* Login with OTP */}
            <Button
              fullWidth
              variant="contained"
              disabled={otpLoading}
              startIcon={otpLoading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                background: 'linear-gradient(to right, #214F7D, #3A8139)',
                color: 'white',
                borderRadius: "50px"
              }}
              onClick={handleSendOtp}
            >
              {otpLoading ? 'Sending OTP...' : 'Login with OTP'}
            </Button>

            {/* Register Link */}
            {!window.cordova ?
              <Link underline="hover" sx={{ display: 'block', mt: 3, fontSize: 14, textDecoration: "underline" }} className='cursor-pointer' onClick={() => navigate("/register?Dr-Huzaifa-Khorakiwala-IN-0000021-IAPM")}>
                Register as I Am Peacekeeper
              </Link> : ""
            }

            {/* Forgot Password Link */}
            <Link
              underline="hover"
              sx={{
                display: 'block',
                mt: 2,
                fontSize: 14,
                textDecoration: "underline",
                color: '#666'
              }}
              className='cursor-pointer'
              onClick={() => navigate("/forgotpassword")}
            >
              Forgot Password?
            </Link>

            {/* Admin Login Link */}
            {/* <Link
              underline="hover"
              sx={{
                display: 'block',
                mt: 1,
                fontSize: 14,
                textDecoration: "underline",
                color: '#214F7D',
                fontWeight: 500
              }}
              className='cursor-pointer'
              onClick={() => navigate("/adminlogin")}
            >
              Admin Login
            </Link> */}
          </>
        )}
      </Paper>

      {/* OTP Modal */}
      <Dialog
        open={showOtpModal}
        onClose={handleCloseOtpModal}
        maxWidth="xs"
        fullWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            minWidth: 280,
            maxWidth: 320
          }
        }}
        sx={{
          '& .MuiDialog-paper': {
            margin: 2
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'left',
          pb: 1.5,
          pt: 2.5,
          px: 2.5,
          background: 'linear-gradient(135deg, #214F7D 0%, #3A8139 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
              Verify OTP
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
              Enter the OTP sent to {email}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseOtpModal}
            size="small"
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{
          py: 1.5,
          px: 2.5,
          '&:last-child': { pb: 2.5 }
        }}>
          {/* Success Alert */}
          {otpSuccess && (
            <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2, fontSize: '0.875rem' }}>
              OTP verified successfully! Redirecting...
            </Alert>
          )}

          {/* Error Alert */}
          {otpError && (
            <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontSize: '0.875rem' }}>
              {otpError}
            </Alert>
          )}

          {/* OTP Input - Hide when success */}
          {!otpSuccess && (
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ textAlign: "left", mb: 1, fontWeight: '600', fontSize: '0.9rem' }}>
                {/* OTP Code */}
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter OTP'
                type="number"
                required
                variant="outlined"
                size="small"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.1rem',
                    letterSpacing: '0.3rem',
                    fontWeight: '500'
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      }
                    }
                  }
                }}
              />
            </Box>
          )}

          {/* Resend and Verify Buttons - Hide when success */}
          {!otpSuccess && (
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleResendOtp}
                  disabled={otpLoading || resendTimer > 0}
                  size="small"
                  sx={{
                    borderColor: '#60778E',
                    color: '#60778E',
                    borderRadius: "50px",
                    fontSize: '0.8rem',
                    py: 1,
                    '&:hover': {
                      borderColor: '#4a5d6b',
                      backgroundColor: 'rgba(96, 119, 142, 0.04)'
                    }
                  }}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                </Button>
              </Grid>

              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleVerifyOtp}
                  disabled={otpLoading || !otp.trim()}
                  startIcon={otpLoading ? <CircularProgress size={18} color="inherit" /> : null}
                  size="small"
                  sx={{
                    background: 'linear-gradient(to right, #214F7D, #3A8139)',
                    color: 'white',
                    borderRadius: "50px",
                    py: 1,
                    '&:hover': {
                      background: 'linear-gradient(to right, #1a3f63, #2d6a2e)',
                    }
                  }}
                >
                  {otpLoading ? 'Verifying...' : 'Verify OTP'}
                </Button>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          {/* Cancel button removed - close icon is now in the title */}
        </DialogActions>
      </Dialog>

      {/* Payment Modal */}
      <Dialog
        open={paymentModalOpen}
        onClose={resetPaymentModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'left',
          color: 'white',
          background: 'linear-gradient(135deg, #214F7D 0%, #3A8139 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 1,
          borderRadius: '12px 12px 0 0'
        }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Payment Required
          </Typography>
          <IconButton
            onClick={resetPaymentModal}
            size="small"
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{
          py: 1.5,
          px: 2.5,
          '&:last-child': { pb: 2.5 }
        }}>
          {!showPaymentForm ? (
            // Initial payment modal content
            <Box sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="h6" color="error" gutterBottom>
                {/* Payment Status: {paymentModalData?.payment_status || 'PENDING'} */}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                Your account requires payment completion to proceed. Current status:
                <strong> { paymentModalData?.payment_status || 'PENDING'}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please complete your payment to access your account.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleShowPaymentForm}
                  sx={{
                    background: 'linear-gradient(to right, #214F7D, #3A8139)',
                    color: 'white',
                    borderRadius: "50px",
                    px: 3
                  }}
                >
                  Complete Payment
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetPaymentModal}
                  sx={{
                    borderColor: '#666',
                    color: '#666',
                    borderRadius: "50px",
                    px: 3
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            // Payment form content
            <Box sx={{ py: 1.5 }}>
              {updatingPaymentStatus ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={60} sx={{ mb: 2, color: '#3A8139' }} />
                  <Typography variant="h6" color="primary" gutterBottom>
                    Updating Account Status...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please wait while we update your payment status.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    This may take a few moments...
                  </Typography>
                </Box>
              ) : paymentSuccess ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    ✅ Payment Completed Successfully!
                  </Typography>

                  {statusUpdateSuccess ? (
                    <>
                      <Typography variant="body1" sx={{ mb: 2.5 }}>
                        Your payment has been completed successfully and your account has been updated. You can now login to your account.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={resetPaymentModal}
                        sx={{
                          background: 'linear-gradient(to right, #214F7D, #3A8139)',
                          color: 'white',
                          borderRadius: "50px",
                          px: 3
                        }}
                      >
                        Close
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="body1" sx={{ mb: 2.5 }}>
                        Your payment has been completed successfully. However, there was an issue updating your account status.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          onClick={resetPaymentModal}
                          sx={{
                            background: 'linear-gradient(to right, #214F7D, #3A8139)',
                            color: 'white',
                            borderRadius: "50px",
                            px: 3
                          }}
                        >
                          Close
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={handleRetryPaymentStatusUpdate}
                          disabled={updatingPaymentStatus}
                          sx={{
                            borderColor: '#666',
                            color: '#666',
                            borderRadius: "50px",
                            px: 3
                          }}
                        >
                          {updatingPaymentStatus ? 'Updating...' : 'Retry Status Update'}
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2, textAlign: 'center' }}>
                    Complete Your Payment
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    Amount: $21.00 USD
                  </Typography>

                  {/* Stripe Payment Form */}
                  <Elements stripe={stripePromise} options={options}>
                    <CheckoutForm
                      email={paymentModalData?.email || email}
                      onPaymentStart={handlePaymentStart}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentError={handlePaymentError}
                      onPaymentComplete={handlePaymentComplete}
                    />
                  </Elements>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Login;
